/**
 * The role-level scheduling modal bodies: blocking the selected slots, asking
 * the panel about the ones that need a meeting moved, and reminding an
 * interviewer who still owes feedback. Split out of ActionModals.tsx to keep
 * that file under the 500-line limit; ActionModals still owns the switch.
 */
import { useEffect, useState } from 'react';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import * as api from '../../services/taActionBoardApi';
import type {
  AskDestination,
  GradedSlot,
  RoleBoard,
} from '../../types/taActionBoard';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { LanguageToggle, type DraftLanguage } from './LanguageToggle';
import { ChannelPicker } from './ChannelPicker';
import { ModalActions } from './ModalActions';
import {
  COLORS,
  firstNameFromEmail,
  fmtSlotRange,
  fullNameFromEmail,
  isValidSlackChannel,
  slotNumber,
} from './format';

/**
 * Confirmation step for the ROLE-level "📌 Block selected slots": lists the
 * exact slots (date · time · panelist names) the recruiter TICKED in the slot
 * list. The /holds endpoint is only called from onConfirm — never when the
 * modal opens.
 */
export function HoldsBody({
  role,
  slots,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  slots: GradedSlot[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const panelists = (role.scheduling.panel?.interviewers || []).map(fullNameFromEmail);
  return (
    <>
      <ModalHeader>
        <ModalTitle>
          Hold {slots.length} selected slot{slots.length === 1 ? '' : 's'}?
        </ModalTitle>
      </ModalHeader>
      {slots.length > 0 ? (
        <div className="space-y-4">
          <ul className="space-y-2 rounded-lg bg-gray-50 p-3">
            {slots.map((s: GradedSlot, i: number) => (
              <li key={s.start} className="flex flex-wrap items-baseline gap-x-2 text-sm text-gray-700">
                <span className="shrink-0">{slotNumber(i)}</span>
                <span className="font-medium">{fmtSlotRange(s)}</span>
                {panelists.length > 0 && (
                  <span className="text-gray-500">— {panelists.join(' & ')}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600">
            The panelists get a tentative calendar entry as an invite — no notification email. Each
            hold then appears under “Holds” in the panel section, where you can see who it was
            proposed to and release it. Confirming an interview releases only the holds proposed to
            that candidate.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-600">No workable slots to hold.</p>
      )}
      <ModalActions
        confirmLabel="Hold selected slots"
        confirmDisabled={slots.length === 0}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
}

const DEST_DMS = 'dms';
const DEST_CHANNEL = 'channel';

/**
 * "💬 Ask panelists…" — the recruiter chooses where the Slack message goes:
 * direct messages to the panelists (default, current behavior) or ONE message
 * to a chosen channel ("#name" or a C… channel id). The /ask-panelists
 * endpoint is only called from onConfirm with the selected destination.
 */
export function AskBody({
  role,
  slots,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  /** The slots the recruiter ticked — only 🟡 ones are worth asking about */
  slots: GradedSlot[];
  /** `message` is the recruiter's text — they read and may rephrase it first. */
  /**
   * `message` is the joint text (channel posts). `messages` is the per-recipient
   * DM text keyed by email — a DM says what THAT person has to do, so each one
   * is edited and sent on its own.
   */
  onConfirm: (
    destination: AskDestination,
    message: string,
    messages: Record<string, string>,
    /** What the recruiter previewed, so the server's fallbacks agree with it. */
    language?: DraftLanguage
  ) => void;
  onCancel: () => void;
}) {
  const panel = role.scheduling.panel;
  const names = (panel?.interviewers || []).map(firstNameFromEmail).join(' & ');
  const n = role.scheduling.candidates.length;
  const askable = slots.filter((s) => s.grade === 'needs-move');
  const skippedFree = slots.length - askable.length;
  // The real message, built by the SAME server code that sends it — so what the
  // recruiter reads here is what the panelists get, and editing it is safe
  // (@channel broadcasts and hidden link syntax are stripped server-side).
  const [message, setMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(true);
  /**
   * What the SERVER could not put in the message. It re-grades the calendars
   * when building the preview, so a slot ticked minutes ago may be free by now
   * or out of the window entirely — and those used to disappear without a word,
   * which is how a recruiter came to approve a message naming fewer yellow
   * options than they had ticked (reported Sep 2026).
   */
  const [dropped, setDropped] = useState<api.AskPreview | null>(null);
  /**
   * One editable DM per recipient, keyed by email.
   *
   * A DM is addressed to ONE person, so it carries only THAT person's items —
   * and only the people with something to move get one. Editing them
   * separately is the point: a correction meant for one panelist must not
   * appear in a colleague's message.
   */
  const [panelistAsks, setPanelistAsks] = useState<api.PanelistAsk[]>([]);
  const [dmEdits, setDmEdits] = useState<Record<string, string>>({});
  /** The whole panel, to name whoever is getting no DM. */
  const [fullPanel, setFullPanel] = useState<string[]>([]);
  /** Set once from the server's suggestion; the recruiter still chooses. */
  const [destTouched, setDestTouched] = useState(false);
  /**
   * Which language the hiring team reads.
   *
   * TWO values on purpose. `chosen` is null until the recruiter picks one, and
   * it alone is in the fetch's dependencies — seeding the displayed value from
   * the first preview would otherwise change a dependency and fetch the same
   * ask a second time, on every open. `rendered` is the language of the
   * message CURRENTLY on screen, as the server reports it; it is what the
   * toggle falls back to before anyone has chosen, and — because the old text
   * deliberately stays visible during a re-fetch — it is also how `staleDraft`
   * below knows the two have come apart.
   */
  const [chosen, setChosen] = useState<DraftLanguage | null>(null);
  const [rendered, setRendered] = useState<DraftLanguage | null>(null);
  const language = chosen ?? rendered;
  /**
   * The message on screen is in the language the recruiter just switched AWAY
   * from. Confirming here would send that one, which is the opposite of what
   * they asked for a moment ago.
   */
  const staleDraft = chosen !== null && rendered !== null && rendered !== chosen;
  const [destType, setDestType] = useState<typeof DEST_DMS | typeof DEST_CHANNEL>(DEST_DMS);
  useEffect(() => {
    let live = true;
    // No synchronous setState here: on a re-fetch the previous message stays on
    // screen until the new one lands, which reads better than a flash of
    // "Building…" — and setting state synchronously in an effect cascades.
    api
      .previewAskPanelists({
        postingId: role.postingId,
        slots: askable,
        candidateCount: n,
        ...(chosen ? { language: chosen } : {}),
      })
      .then((r) => {
        if (!live) return;
        setMessage(r.message);
        // What this message is actually written in. Display and staleness
        // only — never a fetch dependency, or this effect would re-run. A
        // proxy too old to report it is taken at its word that it honoured
        // the request, so the toggle does not lock on a mixed deploy.
        setRendered(r.language ?? chosen ?? null);
        setDropped(r);
        setPanelistAsks(r.panelists ?? []);
        setDmEdits({});
        setFullPanel(r.panel ?? []);
        // Default to what fits this proposal — the wrong default is what sent
        // a panelist with no todo a DM about someone else's meeting.
        if (!destTouched && r.suggestedDestination) {
          setDestType(r.suggestedDestination === 'channel' ? DEST_CHANNEL : DEST_DMS);
        }
      })
      .catch(() => {
        if (!live) return;
        setMessage('');
        setDropped(null);
        setPanelistAsks([]);
      })
      .finally(() => live && setLoadingMessage(false));
    return () => {
      live = false;
    };
    // Re-fetch when the ticked slots change — the message names them — or when
    // the language does, since the whole ask is rebuilt in the other one.
  }, [role.postingId, n, chosen, askable.map((s) => s.start).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const droppedCount =
    (dropped?.unknown?.length ?? 0) + (dropped?.overCap?.length ?? 0) + (dropped?.skippedFree?.length ?? 0);
  const [channel, setChannel] = useState('');
  const channelValue = channel.replace(/\s+/g, '');
  const isChannel = destType === DEST_CHANNEL;
  const dmFor = (email: string) =>
    dmEdits[email] ?? panelistAsks.find((p) => p.email === email)?.message ?? '';
  const idle = fullPanel.filter((e) => !panelistAsks.some((p) => p.email === e));
  /**
   * Does the answering proxy differentiate per panelist?
   *
   * It deploys separately from this frontend, so an older one returns only the
   * joint `message`. Falling back to it keeps DMs working exactly as before
   * rather than disabling the default destination until the proxy catches up.
   */
  const perPerson = panelistAsks.length > 0;
  const valid =
    !loadingMessage &&
    !staleDraft &&
    !!panel?.interviewers?.length &&
    askable.length > 0 &&
    (isChannel
      ? message.trim().length > 0 && isValidSlackChannel(channelValue)
      : perPerson
        ? panelistAsks.every((p) => dmFor(p.email).trim().length > 0)
        : message.trim().length > 0);
  return (
    <>
      <ModalHeader>
        <ModalTitle>Ask panelists on Slack?</ModalTitle>
      </ModalHeader>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {`${names || 'The panelists'} get this Slack message. Read it over — you can rephrase anything before it goes. No candidate names are shared.`}
        </p>
        <div className="space-y-1.5 rounded-lg bg-gray-50 p-3">
          <span className="block text-xs font-semibold text-gray-500">
            Slots in the message ({askable.length})
          </span>
          {askable.map((s) => (
            <p key={s.start} className="text-sm text-gray-700">
              <span
                role="img"
                aria-label="Needs a meeting moved"
                title="Needs a meeting moved"
                className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                style={{ backgroundColor: COLORS.statusOrange }}
              />
              {fmtSlotRange(s)}
              {/* EVERY meeting is named, never "(+1 more)". Which meetings have to
                  move is the whole question being asked, and a count told the
                  recruiter nothing they could act on (Theresa, Sep 2026). */}
              {s.conflicts.length > 0 && (
                <span className="text-gray-500">
                  {' '}
                  —{' '}
                  {s.conflicts
                    .map((c) => `${firstNameFromEmail(c.email)} moves “${c.title}”`)
                    .join('; ')}
                </span>
              )}
            </p>
          ))}
          {askable.length === 0 && (
            <p className="text-sm text-gray-500">
              Nothing to ask about — tick a slot that needs a meeting moved.
            </p>
          )}
          {skippedFree > 0 && (
            <p className="text-xs text-gray-400">
              {skippedFree} free slot{skippedFree === 1 ? '' : 's'} left out — those need no asking,
              just hold them.
            </p>
          )}
          {/* Anything the SERVER could not include, named before the recruiter approves the text. */}
          {droppedCount > 0 && (
            <p
              className="rounded px-1.5 py-1 text-xs"
              style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
            >
              {dropped?.unknown?.length
                ? `${dropped.unknown.length} ticked slot${dropped.unknown.length === 1 ? '' : 's'} ${
                    dropped.unknown.length === 1 ? 'is' : 'are'
                  } no longer in the panel’s graded window (a calendar changed) — press Refresh on the panel and pick again. `
                : ''}
              {dropped?.skippedFree?.length
                ? `${dropped.skippedFree.length} ticked slot${dropped.skippedFree.length === 1 ? '' : 's'} ${
                    dropped.skippedFree.length === 1 ? 'has' : 'have'
                  } become free for the whole panel — hold ${dropped.skippedFree.length === 1 ? 'it' : 'them'} instead. `
                : ''}
              {dropped?.overCap?.length
                ? `${dropped.overCap.length} ticked slot${dropped.overCap.length === 1 ? '' : 's'} exceed${
                    dropped.overCap.length === 1 ? 's' : ''
                  } the ${dropped.maxSlots ?? 10}-slot limit for one message — untick ${
                    dropped.overCap.length === 1 ? 'it' : 'them'
                  } and send a second message. `
                : ''}
              The message above names {dropped?.slotCount ?? 0} slot
              {(dropped?.slotCount ?? 0) === 1 ? '' : 's'}.
            </p>
          )}
        </div>
        <Select value={destType} onValueChange={(v) => {
            // Once the recruiter picks, the server's suggestion stops overriding it.
            setDestTouched(true);
            setDestType(v as typeof destType);
          }}>
          {/* w-full: the DS trigger defaults to w-max, which cut the value off */}
          <SelectTrigger label="Send to" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEST_DMS}>Direct messages to the panelists</SelectItem>
            <SelectItem value={DEST_CHANNEL}>Slack channel…</SelectItem>
          </SelectContent>
        </Select>
        {!isChannel && perPerson && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              One message each, carrying only that person&rsquo;s own items.
              {idle.length > 0 && (
                <>
                  {' '}
                  <span className="text-gray-500">
                    {idle.map(fullNameFromEmail).join(' & ')} {idle.length === 1 ? 'has' : 'have'} nothing to
                    move, so {idle.length === 1 ? 'they get' : 'they get'} no message.
                  </span>
                </>
              )}
            </p>
            {panelistAsks.map((p) => (
              <div key={p.email} className="space-y-1.5">
                <label
                  htmlFor={`ta-ask-dm-${p.email}`}
                  className="block text-sm font-medium text-gray-700"
                >
                  {fullNameFromEmail(p.email)}{' '}
                  <span className="font-normal text-gray-500">
                    — {p.moves === 0 ? 'nothing to move' : `${p.moves} to move`}
                  </span>
                </label>
                <Textarea
                  id={`ta-ask-dm-${p.email}`}
                  rows={11}
                  value={dmFor(p.email)}
                  onChange={(e) => setDmEdits((prev) => ({ ...prev, [p.email]: e.target.value }))}
                  className="font-mono text-xs"
                />
              </div>
            ))}
            <p className="text-xs text-gray-500">
              Slack formatting: *bold*. @channel and @here are stripped before sending.
            </p>
          </div>
        )}
        {/*
          The hiring team's language, not the candidate's — an English-speaking
          panel on a German-tagged role reads English, and the tags cannot know.
          Re-fetches the whole ask, so the channel post AND every DM move
          together; no `hasEdits` guard because the server owns this text and a
          switch is a re-draft the recruiter just asked for.
        */}
        {language && (
          <LanguageToggle value={language} onChange={setChosen} label="Panel reads" />
        )}
        {(isChannel || !perPerson) && (
        <div className="space-y-1.5">
          <label htmlFor="ta-ask-message" className="block text-sm font-medium text-gray-700">
            Message
          </label>
          <Textarea
            id="ta-ask-message"
            rows={12}
            value={loadingMessage ? 'Building the message…' : message}
            disabled={loadingMessage}
            onChange={(e) => setMessage(e.target.value)}
            className="font-mono text-xs"
          />
          <p className="text-xs text-gray-500">
            Slack formatting: *bold*. @channel and @here are stripped before sending.
          </p>
        </div>
        )}
        {/* Only rendered when "Slack channel…" is selected. Static label ABOVE
            the field — the DS floating label sits over the placeholder text. */}
        {isChannel && (
          <div className="space-y-1.5">
            <label htmlFor="ta-ask-channel" className="block text-sm font-medium text-gray-700">
              Channel
            </label>
            <ChannelPicker id="ta-ask-channel" value={channel} onChange={setChannel} />
            <p className="text-xs text-gray-500">
              ONE message goes to this channel instead of individual DMs — the bot must be a member
              (or the channel public).
            </p>
          </div>
        )}
      </div>
      <ModalActions
        confirmLabel={isChannel ? 'Send to channel' : 'Send Slack DMs'}
        confirmDisabled={!valid}
        onConfirm={() =>
          onConfirm(
            isChannel ? { type: 'channel', channel: channelValue } : { type: 'dms' },
            message,
            Object.fromEntries(panelistAsks.map((p) => [p.email, dmFor(p.email)])),
            language ?? undefined
          )
        }
        onCancel={onCancel}
      />
    </>
  );
}
